import {
  CheckCircledIcon,
  InfoCircledIcon,
  PauseIcon,
  PlayIcon,
} from '@radix-ui/react-icons';
import { Flex, Spinner } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import { DotDotDot } from 'components/common/layout/dot-dot-dot';
import { MachineCameraStreamViewer } from 'components/machine/camera-stream-viewer';
import {
  StreamingContext,
  StreamingProvider,
} from 'components/machine/camera-stream-viewer/context';
import { MachineContext } from 'contexts/machine.context';
import { IButton } from 'interfaces/i-buttons';
import { IBaseDialog, IFullDialog } from 'interfaces/i-dialogs';
import { StreamMode } from 'interfaces/i-streaming';
import { CameraSource, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { HardwareVersion } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ICalibrateStereoResponseMsg } from 'lib_ts/interfaces/machine-msg/i-calibrate-stereo';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

const COMPONENT_NAME = 'StereoCheckDialog';

const CalibrationCallout = (props: { msg: ICalibrateStereoResponseMsg }) => {
  const { msg } = props;

  if (msg.success) {
    const text = useMemo(
      () =>
        [
          'Calibration successful',
          msg.py_ft !== undefined
            ? `Measured distance to plate: ${msg.py_ft.toFixed(2)} ft`
            : undefined,
        ]
          .filter((s) => !!s)
          .join(HINT_DELIMITER),
      [msg.py_ft]
    );

    return (
      <CommonCallout
        color={RADIX.COLOR.SUCCESS}
        icon={<CheckCircledIcon />}
        text={text}
      />
    );
  }

  return (
    <CommonCallout
      color={RADIX.COLOR.DANGER}
      text="Calibration failed, please try again or contact support."
    />
  );
};

interface IBaseProps {
  machineID: string;
  hwVersion: HardwareVersion;
  mode: StreamMode;
  asStandalone?: boolean;
  hideCalibration?: boolean;
}

// no need to provide IBaseDialog props when rendering as a standalone component
type IDialogProps = IBaseProps & IBaseDialog;
type IProps = IDialogProps | IBaseProps;

// ! deprecate this view once we have the new dialogs working
export const StereoCheckDialogHoC = (props: IProps) => {
  return (
    <StreamingProvider
      machineID={props.machineID}
      hwVersion={props.hwVersion}
      defaultSource={CameraSource.STEREO_CHECK}
    >
      <StereoCheckDialog {...props} />
    </StreamingProvider>
  );
};

interface IStandaloneProps {
  content: React.ReactNode;
  buttons: IButton[];
}

const StereoCheckStandalone = ({ content, buttons }: IStandaloneProps) => (
  <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
    {content}
    <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
      {buttons
        .filter((b) => !b.invisible)
        .map((b) => (
          <DialogButton key={b.label} {...b} />
        ))}
    </Flex>
  </Flex>
);

// ! deprecate this
const StereoCheckDialog = (props: IProps) => {
  const { setSpecialMode } = useContext(MachineContext);
  const { paused, lastFrame, togglePaused } = useContext(StreamingContext);

  const [loading, setLoading] = useState(false);
  const [lastMessage, setLastMessage] = useState<ICalibrateStereoResponseMsg>();

  const autoCloseTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      clearTimeout(autoCloseTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (props.mode === 'self') {
      setSpecialMode('stereo-check');
    }

    return () => {
      if (props.mode === 'self') {
        setSpecialMode(undefined);
      }
    };
  }, [props.mode]);

  useEffect(() => {
    const callback = (event: CustomEvent) => {
      const data: ICalibrateStereoResponseMsg = event.detail;

      if (!props.hideCalibration) {
        setLastMessage(data);
        setLoading(false);
      }
    };

    WebSocketHelper.on(WsMsgType.M2U_CalibrateStereo, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_CalibrateStereo, callback);
    };
  }, [props.hideCalibration]);

  const buttons: IButton[] = [
    {
      icon: paused ? <PlayIcon /> : <PauseIcon />,
      label: paused ? 'common.resume' : 'common.pause',
      color: RADIX.COLOR.SECONDARY,
      invisible: !lastFrame,
      onClick: () => togglePaused(),
    },
    {
      label: 'settings.calibrate',
      disabled: props.mode !== 'self' || !lastFrame?.cal_board_detected,
      invisible: props.hideCalibration,
      onClick: () => {
        setLastMessage(undefined);
        setLoading(true);

        MachineContextHelper.sendCalibrateVision({
          machineID: props.machineID,
          source: COMPONENT_NAME,
        });
      },
    },
  ];

  if (!props.asStandalone && 'onClose' in props) {
    const dialogProps: IFullDialog = {
      identifier: COMPONENT_NAME,
      width: RADIX.DIALOG.WIDTH.LG,
      title: 'settings.calibrate',
      content: (
        <Flex direction="column" gap={RADIX.FLEX.GAP.LG}>
          <MachineCameraStreamViewer machineID={props.machineID} hideSource />

          {lastMessage ? (
            <CalibrationCallout msg={lastMessage} />
          ) : loading ? (
            <CommonCallout
              color={RADIX.COLOR.INFO}
              icon={<Spinner />}
              content={<DotDotDot label="Calibrating" delay_ms={500} />}
            />
          ) : (
            <CommonCallout
              color={RADIX.COLOR.INFO}
              icon={<InfoCircledIcon />}
              text="Please ensure the lane is clear and the checker board is present before calibrating."
            />
          )}
        </Flex>
      ),
      buttons,
      onClose: props.onClose,
    };
    return (
      <ErrorBoundary identifier={COMPONENT_NAME}>
        <CommonDialog {...dialogProps} />
      </ErrorBoundary>
    );
  }

  const standaloneProps: IStandaloneProps = {
    content: (
      <Flex direction="column" gap={RADIX.FLEX.GAP.LG}>
        <MachineCameraStreamViewer machineID={props.machineID} hideSource />
      </Flex>
    ),
    buttons,
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <StereoCheckStandalone {...standaloneProps} />
    </ErrorBoundary>
  );
};
